import { useEffect } from "react";

/**
 * Keeps a `--app-height` CSS variable in sync with the *actually visible*
 * viewport, and publishes the browser-chrome overlap as `--chrome-inset-bottom`.
 *
 * Why not just use `100dvh`: on mobile Safari and Chrome the URL bar is an
 * overlay, not part of the layout. `dvh` resolves against the viewport as the
 * browser reports it, which during the collapsed/expanded transition (and while
 * the on-screen keyboard is up) does not match what the user can actually see —
 * so a full-height, non-scrolling screen gets its bottom row hidden underneath
 * the URL bar.
 *
 * `window.visualViewport` reports the true visible rect, so we measure it
 * directly and expose it as a CSS variable. `100dvh` stays the fallback for
 * browsers without the API (all evergreen ones have it).
 *
 * `--chrome-inset-bottom` is the gap between the visual viewport's bottom edge
 * and the layout viewport's — i.e. exactly how much bottom chrome is currently
 * overlaying the page — so a fixed bottom row can lift itself clear of it.
 */
export const useAppHeight = () => {
  useEffect(() => {
    const vv = window.visualViewport;

    const apply = () => {
      const root = document.documentElement;
      const height = vv ? vv.height : window.innerHeight;
      root.style.setProperty("--app-height", `${Math.round(height)}px`);

      // How much browser chrome (URL bar / toolbar) currently overlays the
      // bottom of the layout viewport. `offsetTop` accounts for the visual
      // viewport being scrolled within the layout viewport (pinch-zoom).
      const bottomInset = vv
        ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
        : 0;
      root.style.setProperty("--chrome-inset-bottom", `${Math.round(bottomInset)}px`);
    };

    apply();

    // visualViewport fires resize as the URL bar collapses/expands and scroll
    // as it is panned; window resize/orientationchange cover the rest.
    vv?.addEventListener("resize", apply);
    vv?.addEventListener("scroll", apply);
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);

    return () => {
      vv?.removeEventListener("resize", apply);
      vv?.removeEventListener("scroll", apply);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
    };
  }, []);
};

export default useAppHeight;
